import React, { useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ThumbnailHoverModal from '../ThumbnailHoverModal';
import spotlightService from '../../services/SpotlightService';
import { cacheService } from '../../services/CacheService';

const ResultCard = ({ result = {}, index, onHoverModalShow, onHoverModalHide, onOpenPDPModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Ensure result is always an object, even if it's undefined or null
  const safeResult = result || {};

  // Get the original image URL.
  // Sports.TV: always use the channel poster first (same order as ChannelCard,
  // issue #20); portrait poster fields only, no landscape thumbnail (#30).
  const originalImageUrl =
    safeResult.poster ||
    safeResult.spotlight_poster ||
    (safeResult.image && (safeResult.image.poster || safeResult.image.spotlight_poster));

  // Manually construct the image URL with optimized dimensions for search results
  let imageUrl;
  if (originalImageUrl) {
    if (originalImageUrl.startsWith('http')) {
      // If it's a full URL, append the sizing parameters
      const url = new URL(originalImageUrl);
      const imageId = url.pathname.split('/').pop();
      const imagesBaseUrl =
        window.IMAGES_BASE_URL || 'https://sportstvdspmedia.cachefly.net/images';
      // Higher quality for search results: 400px wide for better clarity
      imageUrl = `${imagesBaseUrl}/${imageId}/400/600?webp=1`;
    } else {
      // If it's just an ID, construct the full URL
      const imagesBaseUrl =
        window.IMAGES_BASE_URL || 'https://sportstvdspmedia.cachefly.net/images';
      // Higher quality for search results: 400px wide for better clarity
      imageUrl = `${imagesBaseUrl}/${originalImageUrl}/400/600?webp=1`;
    }
  } else {
    // Fallback for missing images - higher quality dimensions
    imageUrl = 'https://via.placeholder.com/400x600?text=No+Image';
  }

  const title = safeResult.title || safeResult.name || 'Untitled Channel';

  // State for tracking image load errors
  const [imageError, setImageError] = useState(false);

  const cardRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  // Handle image load errors
  const handleImageError = () => {
    setImageError(true);
  };

  // Determine if this is a movie or series based on result properties
  const isMovie =
    safeResult.programming_type === 'Movie' ||
    safeResult.programming_type === 'Full Movie' ||
    safeResult.type === 'movie' ||
    safeResult.content_type === 'movie';

  const isSeries =
    safeResult.programming_type === 'Series' ||
    safeResult.type === 'series' ||
    safeResult.content_type === 'series';

  // Use result slug instead of ID for navigation
  const resultSlug = safeResult.slug || safeResult.permalink || safeResult._id || safeResult.id;

  // Advanced modal positioning configuration (exact copy from ChannelCard)
  const MODAL_POSITIONING = {
    // Base offsets (adjust these values to fine-tune positioning)
    offsetX: -20, // Horizontal offset in pixels (negative = left, positive = right)
    offsetY: 0, // Vertical offset in pixels (negative = up, positive = down)

    // Style-specific offsets
    styleOffsets: {
      default: {
        offsetX: -40, // Default offset for regular cards
        offsetY: -20,
      },
    },

    // Responsive offsets based on screen size
    responsiveOffsets: {
      mobile: {
        // Screen width <= 768px
        offsetX: -10,
        offsetY: 0, // Same as ChannelCard - no upward offset
      },
      tablet: {
        // Screen width <= 1024px
        offsetX: -15,
        offsetY: 0, // Same as ChannelCard - no upward offset
      },
      desktop: {
        // Screen width > 1024px
        offsetX: -20,
        offsetY: 0, // Same as ChannelCard - no upward offset
      },
    },

    // Position anchoring options
    anchor: {
      horizontal: 'left', // 'left', 'center', 'right' - where to anchor horizontally on the card
      vertical: 'top', // 'top', 'center', 'bottom' - where to anchor vertically on the card
    },

    // Viewport boundary handling
    boundaryHandling: {
      enabled: true, // Enable automatic boundary detection
      padding: 20, // Minimum distance from viewport edges
      fallbackToCenter: false, // If true, center modal when it would go off-screen
    },

    // Debug mode - set to true to see positioning info in console
    debug: false,
  };

  // Advanced modal position calculation (container-relative)
  const calculateModalPosition = cardElement => {
    if (!cardElement) {return { x: 0, y: 0 };}

    const rect = cardElement.getBoundingClientRect();
    
    // Find the results container (the positioned parent)
    const resultsContainer = cardElement.closest('.relative');
    const containerRect = resultsContainer ? resultsContainer.getBoundingClientRect() : { left: 0, top: 0 };
    
    // Calculate position relative to the container instead of viewport
    const viewport = {
      width: resultsContainer ? resultsContainer.clientWidth : window.innerWidth,
      height: resultsContainer ? resultsContainer.clientHeight : window.innerHeight,
    };

    // Get current style for style-specific offsets
    const currentStyle = 'default'; // Search results use default style

    // Get responsive offset based on screen size
    let responsiveOffset = MODAL_POSITIONING.responsiveOffsets.desktop;
    if (window.innerWidth <= 768) {
      responsiveOffset = MODAL_POSITIONING.responsiveOffsets.mobile;
    } else if (window.innerWidth <= 1024) {
      responsiveOffset = MODAL_POSITIONING.responsiveOffsets.tablet;
    }

    // Get style-specific offset
    const styleOffset =
      MODAL_POSITIONING.styleOffsets[currentStyle] || MODAL_POSITIONING.styleOffsets.default;

    // Calculate base position based on anchor points (relative to container)
    let baseX, baseY;

    // Horizontal anchoring (relative to container)
    switch (MODAL_POSITIONING.anchor.horizontal) {
      case 'center':
        baseX = (rect.left - containerRect.left) + rect.width / 2;
        break;
      case 'right':
        baseX = (rect.right - containerRect.left);
        break;
      case 'left':
      default:
        baseX = (rect.left - containerRect.left);
        break;
    }

    // Vertical anchoring (relative to container)
    switch (MODAL_POSITIONING.anchor.vertical) {
      case 'center':
        baseY = (rect.top - containerRect.top) + rect.height / 2;
        break;
      case 'bottom':
        baseY = (rect.bottom - containerRect.top);
        break;
      case 'top':
      default:
        baseY = (rect.top - containerRect.top);
        break;
    }

    // Apply offsets (priority: style-specific > responsive > base)
    const finalOffsetX =
      styleOffset.offsetX !== undefined
        ? styleOffset.offsetX
        : responsiveOffset.offsetX !== undefined
        ? responsiveOffset.offsetX
        : MODAL_POSITIONING.offsetX;

    const finalOffsetY =
      styleOffset.offsetY !== undefined
        ? styleOffset.offsetY
        : responsiveOffset.offsetY !== undefined
        ? responsiveOffset.offsetY
        : MODAL_POSITIONING.offsetY;

    let finalX = baseX + finalOffsetX;
    let finalY = baseY + finalOffsetY;

    // Boundary handling (container-relative)
    if (MODAL_POSITIONING.boundaryHandling.enabled) {
      const modalWidth = 416; // Approximate modal width
      const modalHeight = 400; // Approximate modal height
      const padding = MODAL_POSITIONING.boundaryHandling.padding;

      // Check horizontal boundaries (relative to container)
      if (finalX < padding) {
        if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
          finalX = (viewport.width - modalWidth) / 2;
        } else {
          finalX = padding;
        }
      } else if (finalX + modalWidth > viewport.width - padding) {
        if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
          finalX = (viewport.width - modalWidth) / 2;
        } else {
          finalX = viewport.width - modalWidth - padding;
        }
      }

      // Check vertical boundaries (relative to container)
      if (finalY < padding) {
        if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
          finalY = (viewport.height - modalHeight) / 2;
        } else {
          finalY = padding;
        }
      } else if (finalY + modalHeight > viewport.height - padding) {
        if (MODAL_POSITIONING.boundaryHandling.fallbackToCenter) {
          finalY = (viewport.height - modalHeight) / 2;
        } else {
          finalY = viewport.height - modalHeight - padding;
        }
      }
    }

    return { x: finalX, y: finalY };
  };

  // Handle hover modal show (immediate, let SearchResults handle timing)
  const handleHoverModalShow = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (cardRef.current && onHoverModalShow) {
      const position = calculateModalPosition(cardRef.current);
      onHoverModalShow(safeResult, position, index);
    }
  }, [safeResult, index, onHoverModalShow]);

  // Handle hover modal hide (immediate, let SearchResults handle timing)
  const handleHoverModalHide = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (onHoverModalHide) {
      onHoverModalHide();
    }
  }, [onHoverModalHide]);

  // Set up hover prefetching
  const { handleMouseEnter: handlePrefetchEnter, handleMouseLeave: handlePrefetchLeave } =
    useHoverPrefetch(resultSlug, isSeries ? 'series' : 'channel', 250);

  // Combined mouse enter handler
  const handleMouseEnter = useCallback(() => {
    handlePrefetchEnter();
    handleHoverModalShow();
  }, [handlePrefetchEnter, handleHoverModalShow]);

  // Combined mouse leave handler
  const handleMouseLeave = useCallback(() => {
    handlePrefetchLeave();
    handleHoverModalHide();
  }, [handlePrefetchLeave, handleHoverModalHide]);

  // Handle click on result card
  const handleClick = () => {
    // Open PDP modal on the current page instead of navigating
    if (onOpenPDPModal) {
      onOpenPDPModal(safeResult, { autoPlay: false, currentTime: 0 });
    } else {
      // Fallback to navigation if callback not provided (shouldn't happen)
      console.warn('ResultCard: onOpenPDPModal callback not provided, falling back to navigation');
      const resultPath = `/?channel=${resultSlug}`;
      
      if (isMovie) {
        navigate(resultPath, { state: { contentType: 'movie', from: location.pathname } });
      } else if (isSeries) {
        navigate(resultPath, { state: { contentType: 'series', from: location.pathname } });
      } else {
        navigate(resultPath, { state: { contentType: 'channel', from: location.pathname } });
      }
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        className="flex-shrink-0 cursor-pointer channel-card"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="relative overflow-hidden rounded-lg aspect-[2/3] w-full">
          <img
            src={imageError ? 'https://via.placeholder.com/300x450?text=No+Image' : imageUrl}
            alt={title}
            className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
            onError={handleImageError}
            loading="lazy"
          />
        </div>
      </div>
    </>
  );
};

// Custom hook for hover prefetching
const useHoverPrefetch = (channelSlug, type, delay = 300) => {
  const timeoutRef = useRef(null);
  const hasPrefetched = useRef(false);

  const handleMouseEnter = useCallback(() => {
    if (hasPrefetched.current || !channelSlug) {return;}

    timeoutRef.current = setTimeout(async () => {
      try {
        // Check if already cached to avoid redundant requests
        const cacheKey = `channel_${channelSlug}`;
        if (cacheService.get(cacheKey)) {
          return;
        }

        await spotlightService.getChannelBySlug(channelSlug).catch(e => {
          throw new Error(e);
        });

        hasPrefetched.current = true;

      } catch (error) {
        console.warn(`Prefetch failed for channel ${channelSlug}:`, error);
      }
    }, delay);
  }, [channelSlug, delay]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { handleMouseEnter, handleMouseLeave };
};

export default ResultCard;
